import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { BundleState, GetBundleQuery } from 'data/__generated__';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { CopyValue } from 'components/CopyValue';
import { StatusTag } from 'pages/Bundles/components/StatusTag';
import { BundleTypeTag } from 'pages/Bundles/components/TypeTag';
import { DetailLink } from 'components/DetailsView';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { ContainerTypeTag } from 'pages/Bundles/components/ContainerTypeTag';

export const GeneralInfoSection = ({ bundleDetails }: { bundleDetails: GetBundleQuery['bundle'] }) => {
    const {
        _id: bundleId,
        createdAt,
        number,
        numberOfReferences,
        numberOfUnits,
        operatorName,
        scheduledPreparationDate,
        preparationDates,
        preparationDuration,
        state,
        stateHistory,
        type,
        containerType,
        boxes,
    } = bundleDetails;

    const preparationDurationString = preparationDuration
        ? `${Math.floor(preparationDuration / 60)}h ${(preparationDuration % 60).toString().padStart(2, '0')}`
        : '-';

    const hasBeenPaused = !!stateHistory.find(({ state }) => state === BundleState.Paused);

    const theme = useContext(ThemeContext);

    return (
        <SectionContainer title="Informations Générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    <CopyValue value={bundleId} />
                    <DetailValue label="Numéro" value={`#${number}`} />
                    <DetailValue label="Opérateur" value={operatorName} />
                    <DetailValue label="Nombre de références" value={numberOfReferences.toString()} />
                    <DetailValue label="Nombre d'unités" value={numberOfUnits.toString()} />
                    <DetailValue label="Type" value={<BundleTypeTag type={type} />} />
                    <DetailValue label="Type de conteneur" value={<ContainerTypeTag type={containerType} />} />
                    <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                    <DetailValue label="Date de préparation prévue" value={scheduledPreparationDate} />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue
                        label="Préparation"
                        value={
                            <PreparationInfo>
                                <PreparationInterval>
                                    {preparationDates?.start || '?'} &rarr; {preparationDates?.end || '?'}
                                </PreparationInterval>
                                <PreparationDuration>{preparationDurationString}</PreparationDuration>
                                {hasBeenPaused ? <PausedWarning>A été "paused"</PausedWarning> : null}
                            </PreparationInfo>
                        }
                    />
                    <DetailValue label="Statut" value={<StatusTag state={state} />} />
                    <BoxesContainer>
                        <BoxesTitle>Caisses</BoxesTitle>
                        {[...boxes]
                            .sort((boxA, boxB) => (boxA.position || 0) - (boxB.position || 0))
                            .map((box) => (
                                <BoxItem key={box._id}>
                                    <BoxPosition>{box.position}</BoxPosition>
                                    <DetailLink
                                        path="delivery"
                                        value={box.deliveryId}
                                        name={box.label}
                                        color={theme?.textColor}
                                        shouldWrap
                                    />
                                    {box.hasOnsiteSetup && <OnsiteSetupTag>Setup sur site</OnsiteSetupTag>}
                                </BoxItem>
                            ))}
                    </BoxesContainer>
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const PreparationInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const PreparationInterval = styled.div`
    font-size: 16px;
`;

const PreparationDuration = styled.div`
    font-size: 14px;
`;

const PausedWarning = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.warningColor};
`;

const BoxesContainer = styled.div`
    margin-top: 10px;
`;

const BoxesTitle = styled.h4`
    margin-bottom: 10px;
`;

const BoxItem = styled.div`
    margin-bottom: 5px;
    display: flex;
    align-items: center;
`;

const OnsiteSetupTag = styled.span`
    margin-left: 10px;
    font-size: 12px;
    background-color: ${({ theme }) => theme.successColor};
    color: white;
    padding: 2px 5px;
    border-radius: 4px;
`;

const BoxPosition = styled.span`
    width: 20px;
    font-weight: bold;
    color: ${({ theme }) => theme.textColor};
`;
